import EditController from "@/core/EditController";
import { isRequired } from "@/helpers/validators";
import api from "@/core/api";
import { query } from "@/api/db";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import numberToString from "@/helpers/numberToString";
import { edit } from "@/core/db";
import ItemRowEdit from "./ItemRowEdit.vue";

export default class MedicamentWriteoffActsEditController extends EditController {
  itemsGrid: any = null;

  constructor(protected form: any, protected props: any) {
    super(form, props);

    const panelFun: any[] = [];
    if (!this.readonly.value && (props.store.model.access.update || props.store.model.access.create)) {
      panelFun.push({ save: {} });
    }
    panelFun.push({ close: {} });
    this.createPanelFun(panelFun);
  }

  setItemsGrid(grid: any) {
    this.itemsGrid = grid;
  }

  /** Сумма количеств в натуральных единицах (уп. × кол-во в уп. по карточке М-17). */
  sumQuantities(): number {
    const items = this.form.value?.data?.items || [];
    let s = 0;
    for (const it of items) {
      if (it.deletionmark) continue;
      const pkg = parseFloat(it.quantity_packages) || 0;
      const pc = parseFloat(it._package_count) || 1;
      s += pkg * pc;
    }
    return Math.round(s * 1000) / 1000;
  }

  sumAmounts(): number {
    const items = this.form.value?.data?.items || [];
    let s = 0;
    for (const it of items) {
      if (it.deletionmark) continue;
      s += parseFloat(it.line_amount) || 0;
    }
    return Math.round(s * 100) / 100;
  }

  totalQuantityWords(): string {
    const n = this.sumQuantities();
    const w = (numberToString(n, ["целая", "целых", "целых"], null) || "").trim();
    if (!w) return "";
    return w.charAt(0).toUpperCase() + w.slice(1);
  }

  footerMolLine(): string {
    const d = this.form.value?.data;
    if (!d) return "";
    const pos = (d.responsible_position || "").trim();
    const fio = (d.responsible_fio || "").trim();
    if (pos && fio) return `${pos}, ${fio}`;
    return fio || pos || "";
  }

  footerCommissionLine(): string {
    const d = this.form.value?.data;
    if (!d) return "";
    const parts: string[] = [];
    const chair = [d.chair_position, d.chair_fio].filter(Boolean).join(", ");
    if (chair) parts.push(`Председатель: ${chair}`);
    const m1 = [d.member1_position, d.member1_fio].filter(Boolean).join(", ");
    if (m1) parts.push(`Член: ${m1}`);
    const m2 = [d.member2_position, d.member2_fio].filter(Boolean).join(", ");
    if (m2) parts.push(`Член: ${m2}`);
    if (d.commission_text) parts.push(d.commission_text);
    return parts.join(" · ");
  }

  settings() {
    const self = this;
    return Object.assign(this.props.config, {
      onCreate: async (store: any) => {
        if (!store.data.act_date) {
          store.data.act_date = dayjs().format("YYYY-MM-DD");
        }
        if (!store.data.status) {
          store.data.status = "draft";
        }
        const uid = this.props?.store?.state?.user?.consignee_id;
        if (uid && !store.data.warehouse_id) {
          store.data.warehouse_id = uid;
          store.data.department_id = uid;
        }
      },
      onAfterLoad: async (store: any) => {
        if (store?.data?.department_id) {
          await self.fillMolFromDepartment(store.data.department_id, store.data);
        }
      },
      fields: {
        id: { config: { visible: false } },
        act_number: {
          description: "Номер акта",
          validation: { isRequired },
        },
        act_date: {
          description: "Дата",
          validation: { isRequired },
        },
        warehouse_id: {
          description: "Склад (остатки М-17)",
          validation: { isRequired },
          config: {
            reference: "consignees",
            displayField: "name",
            onSelect: async (_data: any, store: any) => {
              const wid = store?.data?.warehouse_id;
              if (wid && !store.data.department_id) {
                store.data.department_id = wid;
              }
            },
          },
        },
        department_id: {
          description: "Отделение (структура учреждения)",
          config: {
            reference: "consignees",
            displayField: "name",
            onSelect: async (data: any, store: any) => {
              const id = data?.id;
              if (id) await self.fillMolFromDepartment(id, store.data);
            },
          },
        },
        responsible_position: { description: "МОЛ (должность):"  },
        responsible_fio: { description: "МОЛ (ФИО):" },
        commission_text: { description: "Комиссия в составе" },
        order_date: { description: "Приказ о комиссии:" },
        order_number: { description: "Приказ о комиссии №:" },
        chair_position: { description: "Председатель (должность):" },
        chair_fio: { description: "Председатель (ФИО):" },
        member1_position: { description: "Член комиссии 1 (должность):" },
        member1_fio: { description: "Член комиссии 1 (ФИО):" },
        member2_position: { description: "Член комиссии 2 (должность):" },
        member2_fio: { description: "Член комиссии 2 (ФИО):" },
        funding_sources_note: {
          description: "В том числе по источникам финансирования",
        },
        status: {
          description: "Статус",
          validation: { isRequired },
        },
        created_at: { config: { visible: false } },
        updated_at: { config: { visible: false } },
        deletionmark: { config: { visible: false } },
        items: {
          table: "medicament_writeoff_act_items",
          autoLoad: true,
          /**
           * DBGrid при «Добавить» вызывает addRow, который подмешивает default — не onCreate.
           * onCreate в index.vue грида вызывается при монтировании с DBGridController (у него нет .data строки).
           */
          default: () => {
            const items = self.form.value?.data?.items || [];
            const max = items.length
              ? Math.max(...items.map((r: any) => parseInt(String(r.position_number), 10) || 0))
              : 0;
            const actId = self.form.value?.data?.id;
            const row: Record<string, any> = {
              position_number: max + 1,
              quantity_packages: 0,
            };
            if (actId) {
              row.medicament_writeoff_act_id = actId;
            }
            return row;
          },
          onBeforeEdit: async (params: any) => {
            const sid = params.data?.medicament_stock_id;
            if (sid) {
              await self.fillItemFromStock(sid, { data: params.data });
            }
            return edit(params);
          },
          onBeforeNew: async (params: any) => edit(params),
          edit: {
            component: ItemRowEdit,
            panel: {
              width: "min(96vw, 760px)",
              modal: true,
            },
            config: {
              fields: self.getItemsFields(),
            },
          },
          config: { sorting: true },
          fields: self.getItemsFields(),
        },
      },
    });
  }

  async fillMolFromDepartment(consigneeId: string, formData: any) {
    if (!consigneeId || !formData) return;
    try {
      const response: any = await query({
        table: "consignees",
        method: "object",
        data: { id: consigneeId },
      });
      const cons = response?.data || response;
      if (!cons) return;
      if (cons.mol_fio) formData.responsible_fio = cons.mol_fio;
      if (cons.position) formData.responsible_position = cons.position;
    } catch (e) {
      console.error(e);
    }
  }

  getItemsFields() {
    const self = this;
    const fmt = (n: number) => {
      const s = n.toFixed(3);
      return s.replace(/\.?0+$/, "");
    };

    return {
      id: { config: { visible: false, hide: true } },
      medicament_writeoff_act_id: { config: { visible: false, hide: true } },
      position_number: {
        description: "№ п/п",
        config: { readonly: true, width: "70px", sticky: true },
      },
      medicament_stock_id: {
        description: "Наименование (карточка М-17)",
        validation: { isRequired },
        config: {
          reference: "medicament_stock",
          displayField: "product_name",
          sticky: true,
          width: "280px",
          typeahead: {
            table: "medicament_stock",
            notion: (row: any) => ({
              title: row.product_name || "",
              notion:
                `<div>${row.product_name || ""}</div>` +
                (row.batch_number ? `<small>Серия: ${row.batch_number}</small>` : "") +
                (row.manufacturer ? `<small> | ${row.manufacturer}</small>` : ""),
            }),
            options: {
              fields: ["id", "product_name", "batch_number", "manufacturer", "expiry_date", "unit_id", "quantity", "warehouse_id"],
              limit: 25,
              filters: () => {
                const wid = self.form.value?.data?.warehouse_id;
                if (!wid) return [];
                return [{ field: "warehouse_id", value: wid }];
              },
            },
          },
          onSelect: async (data: any, store: any) => {
            const stockId = data?.id;
            if (stockId) await self.fillItemFromStock(stockId, store);
          },
        },
      },
      product_name: {
        description: "Наименование",
        config: {
          visible: false,
          readonly: true,
          type: "textarea",
          rows: 2,
        },
      },
      batch_number: { description: "Серия", config: { width: "110px", readonly: true } },
      expiry_date: { description: "Срок годности", config: { width: "120px", readonly: true } },
      manufacturer: { description: "Производитель", config: { width: "160px", readonly: true } },
      unit_id: {
        description: "Ед. изм.",
        config: { width: "90px", readonly: true },
      },
      _stock_balance: {
        description: "Остаток (уп.)",
        config: {
          width: "100px",
          readonly: true,
          render: (_v: any, row: any) => (row._stock_balance != null ? fmt(row._stock_balance) : "—"),
        },
      },
      quantity_packages: {
        description: "Кол-во (уп.)",
        validation: { isRequired },
        config: {
          width: "110px",
          type: "number",
          step: 0.001,
          min: 0,
          onBlur: async (_v: any, rowData: any) => {
            await self.updateLineAmount({ data: rowData });
          },
        },
      },
      line_amount: {
        description: "Сумма, руб.",
        config: {
          width: "110px",
          type: "number",
          step: 0.01,
          min: 0,
        },
      },
      journal_ref: {
        description: "Причина / журнал с № по №",
        config: { width: "200px" },
      },
      created_at: { config: { visible: false } },
      updated_at: { config: { visible: false } },
      deletionmark: { config: { visible: false } },
    };
  }

  async fillItemFromStock(stockId: string, form: any) {
    if (!stockId || !form?.data) return;
    const wid = this.form.value?.data?.warehouse_id;
    try {
      const [stockResponse, availResponse] = await Promise.all([
        query({ table: "medicament_stock", method: "object", data: { id: stockId } }),
        api.query(`/nsi/medicament-stock/${stockId}/available-quantity`, {
          method: "POST",
          data: {},
        }),
      ]);
      const stock = stockResponse?.data || stockResponse;
      const av = (availResponse as any)?.success ? (availResponse as any) : null;
      if (!stock) return;

      if (wid && stock.warehouse_id && stock.warehouse_id !== wid) {
        await Swal.fire({
          icon: "warning",
          title: "Другой склад",
          text: "Выбранная карточка М-17 относится к другому складу, чем в шапке акта.",
        });
        form.data.medicament_stock_id = null;
        return;
      }

      form.data.product_name = stock.product_name || "";
      form.data.batch_number = stock.batch_number || "";
      form.data.expiry_date = stock.expiry_date || null;
      form.data.manufacturer = stock.manufacturer || "";
      form.data.unit_id = stock.unit_id;
      form.data.medicament_stock_id = stockId;

      const pkg = parseFloat(stock.package_count) || 1;
      let availablePkg = parseFloat(stock.quantity) || 0;
      if (av && av.availablePackages != null) {
        availablePkg = parseFloat(av.availablePackages) || availablePkg;
      }
      form.data._stock_balance = availablePkg;
      form.data._package_count = pkg;
      form.data.quantity_packages = availablePkg > 0 ? availablePkg : 0;
      await this.updateLineAmount(form);
    } catch (e) {
      console.error(e);
    }
  }

  async updateLineAmount(form: any) {
    const stockId = form.data?.medicament_stock_id;
    const qty = parseFloat(form.data?.quantity_packages) || 0;
    if (!stockId || qty <= 0) {
      form.data.line_amount = "0.00";
      return;
    }
    const stockResp: any = await query({
      table: "medicament_stock",
      method: "object",
      data: { id: stockId },
    });
    const stock = stockResp?.data || stockResp;
    const packageCount = parseFloat(stock?.package_count) || 1;
    const units = Math.round(qty * packageCount * 1000) / 1000;
    try {
      const response: any = await api.query(`/nsi/medicament-stock/${stockId}/calculate-expense-amount`, {
        method: "POST",
        data: { quantity_units: units },
      });
      if (response?.success) {
        form.data.line_amount = (parseFloat(response.amount) || 0).toFixed(2);
      }
    } catch {
      form.data.line_amount = "0.00";
    }
  }
}
